import { useCallback, useRef } from 'react';
import axios, { AxiosInstance } from 'axios';
import {
  CommonResponse,
  MeshDownloadResponse,
  OrthophotoResponse,
  PointCloudDownloadResponse,
  SnapshotListResponse
} from '../models/maps.model';
import { meissaApi } from './meissa.api';

const baseURL = '/v1/api/meissa-api';

const getApiClient = (accessToken?: string): AxiosInstance => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (accessToken) {
    headers.Authorization = `JWT ${accessToken}`;
  }

  return axios.create({
    baseURL: baseURL,
    headers
  });
};

const mapsApi = {
  /**
   * 포인트 클라우드 다운로드 URL 가져오기
   */
  getPointCloudDownloadUrl: async (
    snapshotId: string | number,
    accessToken: string
  ): Promise<PointCloudDownloadResponse> => {
    const client = getApiClient(accessToken);
    const response = await client.get<CommonResponse<PointCloudDownloadResponse>>(
      `/external/api/resource/point-cloud/snapshot/${snapshotId}/download`
    );

    return response.data.result;
  },

  /**
   * 메시 다운로드 URL 가져오기
   */
  getMeshDownloadUrl: async (
    snapshotId: string | number,
    accessToken: string
  ): Promise<MeshDownloadResponse> => {
    const client = getApiClient(accessToken);
    const response = await client.get<CommonResponse<MeshDownloadResponse>>(
      `/external/api/resource/mesh/snapshot/${snapshotId}/download`,
      { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } }
    );

    return response.data.result;
  },

  /**
   * Orthophoto 정보 가져오기
   */
  getOrthophoto: async (
    snapshotId: string | number,
    accessToken: string
  ): Promise<OrthophotoResponse> => {
    const client = getApiClient(accessToken);
    const response = await client.get<CommonResponse<OrthophotoResponse>>(
      `/external/api/resource/orthophoto/snapshot/${snapshotId}`
    );

    return response.data.result;
  },

  /**
   * 프로젝트별 스냅샷 리스트 가져오기
   */
  getSnapshotList: async (
    projectId: string | number,
    accessToken: string,
    params?: { offset?: number; limit?: number }
  ): Promise<SnapshotListResponse> => {
    const client = getApiClient(accessToken);
    const response = await client.get<SnapshotListResponse>(
      `/external/api/projects/${projectId}/snapshots`,
      {
        params
      }
    );

    return response.data;
  }
};

export const useMeissaApi = (initialTokens?: {
  access?: string;
  refresh?: string;
  projectId?: number;
}) => {
  const accessTokenRef = useRef(initialTokens?.access || '');
  const refreshTokenRef = useRef(initialTokens?.refresh || '');

  // initialTokens가 변경되면 ref도 동기화
  if (initialTokens?.access && initialTokens.access !== accessTokenRef.current) {
    accessTokenRef.current = initialTokens.access;
  }
  if (initialTokens?.refresh && initialTokens.refresh !== refreshTokenRef.current) {
    refreshTokenRef.current = initialTokens.refresh;
  }

  /** Meissa 토큰 재발급 후 새 access token 반환 */
  const refreshMeissaToken = useCallback(async (): Promise<string | null> => {
    try {
      const tokenData = await meissaApi.token();
      if (tokenData?.resultObject) {
        const { access, refresh } = tokenData.resultObject;
        accessTokenRef.current = access;
        refreshTokenRef.current = refresh;
        return access;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  /** 401 발생 시 토큰 재발급 후 자동 재시도 */
  const withTokenRefresh = useCallback(
    async <T>(fn: (token: string) => Promise<T>): Promise<T> => {
      if (!accessTokenRef.current) {
        throw new Error('Not authenticated. Please login first.');
      }
      try {
        return await fn(accessTokenRef.current);
      } catch (error: any) {
        if (error?.response?.status === 401) {
          const newToken = await refreshMeissaToken();
          if (newToken) {
            return await fn(newToken);
          }
        }
        throw error;
      }
    },
    [refreshMeissaToken]
  );

  const getPointCloudUrl = useCallback(
    (snapshotId: string | number) =>
      withTokenRefresh((token) => mapsApi.getPointCloudDownloadUrl(snapshotId, token)),
    [withTokenRefresh]
  );

  const getMeshUrl = useCallback(
    (snapshotId: string | number) =>
      withTokenRefresh((token) => mapsApi.getMeshDownloadUrl(snapshotId, token)),
    [withTokenRefresh]
  );

  const getOrthophoto = useCallback(
    (snapshotId: string | number) =>
      withTokenRefresh((token) => mapsApi.getOrthophoto(snapshotId, token)),
    [withTokenRefresh]
  );

  const getSnapshotList = useCallback(
    (projectId: string | number, params?: { offset?: number; limit?: number }) =>
      withTokenRefresh((token) => mapsApi.getSnapshotList(projectId, token, params)),
    [withTokenRefresh]
  );

  const setTokens = useCallback((access: string, refresh: string) => {
    accessTokenRef.current = access;
    refreshTokenRef.current = refresh;
  }, []);

  return {
    getPointCloudUrl,
    getMeshUrl,
    getOrthophoto,
    getSnapshotList,
    getAccessToken: () => accessTokenRef.current,
    getRefreshToken: () => refreshTokenRef.current,
    setTokens
  };
};
